import { useEffect, useState } from "react";
import { X } from "lucide-react";
// getCvSettings/putCvSettings — ручные значения (GET/PUT /api/cv-reports/settings),
// getCvInputs — подобранные CV-модулем пороги (/api/cv-reports/inputs → autoSettings).
// Если файл лежит не в src/pages — поправить путь.
import { getCvSettings, putCvSettings, getCvInputs } from "../lib/cortivoxApi";

// Диалог «Параметры» у плашки упражнения (раздел «Мимика»). По каждому
// параметру — ползунок со значением, подобранным автоматически (из последней
// тренировки), пациент может передвинуть его вручную (врач: без надписи
// «подбирается автоматически» — просто выставленное число).
// Значения уходят в PUT /api/cv-reports/settings и попадают в тренажёр
// через GET /api/cv-reports/inputs (userSettings[slug]).
// Ошибка 404 здесь значит, что на сервере ещё старый cv_reports.py —
// нужен деплой новой версии + миграции 013.
const REPS_MIN = 1;
const REPS_MAX = 10; // врач: максимум 10, а не 30
const REPS_DEFAULT = 10;
const HOLD_MIN = 0.5;          // время удержания позы, сек
const HOLD_MAX = 5;            // врач: максимум 5 сек, а не 10
const HOLD_DEFAULT = 3;        // пока подобранного значения нет (середина лестницы 1/3/5)
const HOLD_STEP = 0.5;

const roundHalf = (v) => Math.round(v * 2) / 2;
const inRange = (v, [lo, hi]) => Number.isFinite(v) && v >= lo && v <= hi;

// 404 — на сервере ещё старый cv_reports.py: маршрутов /settings там нет.
// Врачу это важнее формального «код 404» — пишем прямо.
const describeError = (e, fallback) =>
  e?.status === 404
    ? "Сервер ещё не обновлён — нужен новый cv_reports.py и рестарт API"
    : e?.message || fallback;

export default function ExerciseParamsDialog({ slug, title, onClose }) {
  const [reps, setReps] = useState(REPS_DEFAULT);
  const [hold, setHold] = useState(HOLD_DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        // Ручные значения (что пациент сохранил раньше) и авто-подобранные
        // (последний отчёт CV) грузим параллельно; ошибка /inputs не мешает:
        // авто-значение — только стартовая точка ползунка.
        const [manualRes, inputsRes] = await Promise.allSettled([
          getCvSettings(slug),
          getCvInputs({ exerciseSlug: slug }),
        ]);
        if (manualRes.status === "rejected") throw manualRes.reason;
        let repsValue = REPS_DEFAULT;
        let holdValue = HOLD_DEFAULT;
        const s = manualRes.value?.settings || {};
        const savedReps = Number(s.max_reps);
        if (savedReps >= REPS_MIN && savedReps <= REPS_MAX) repsValue = savedReps;
        const savedHold = Number(s.target_time_hold);
        if (inRange(savedHold, [HOLD_MIN, HOLD_MAX])) holdValue = savedHold;
        else {
          // Ручного значения нет — показываем подобранное CV (до перекрытия).
          const autoHold = Number(inputsRes.value?.autoSettings?.[slug]?.target_time_hold);
          if (inRange(autoHold, [HOLD_MIN, HOLD_MAX])) holdValue = roundHalf(autoHold);
        }
        setReps(repsValue);
        setHold(holdValue);
      } catch (e) {
        setError(describeError(e, "Не удалось загрузить параметры"));
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // Esc — закрыть, как и у остальных диалогов приложения.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      // Полная замена набора: оба параметра уходят вместе.
      await putCvSettings(slug, { max_reps: Math.round(reps), target_time_hold: roundHalf(hold) });
      setSaved(true);
      setTimeout(() => onClose(), 700); // короткое «Сохранено» — и закрыть
    } catch (e) {
      setError(describeError(e, "Не удалось сохранить параметры"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md rounded-2xl border border-border bg-background p-6 shadow-lg">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold text-primary">Параметры · {title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary hover:text-primary"
            aria-label="Закрыть"
          >
            <X className="size-4" />
          </button>
        </div>

        {loading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Загрузка параметров...</p>
        ) : (
          <div className="flex flex-col gap-5">
            {/* Количество повторов: уходит в offer как user_settings[slug].max_reps */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="cv-params-reps" className="text-sm font-medium text-primary">
                  Количество повторов
                </label>
                <span className="min-w-12 rounded-lg border border-border bg-secondary/50 px-2.5 py-1 text-center text-sm font-semibold text-primary">
                  {reps}
                </span>
              </div>
              <input
                id="cv-params-reps"
                type="range"
                min={REPS_MIN}
                max={REPS_MAX}
                step={1}
                value={reps}
                onChange={(e) => { setReps(Number(e.target.value)); setSaved(false); }}
                className="w-full accent-primary cursor-pointer"
              />
              <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                <span>{REPS_MIN}</span>
                <span>{REPS_MAX}</span>
              </div>
            </div>

            {/* Время удержания позы: стартовое значение — подобранное CV
                (autoSettings.target_time_hold из последней тренировки),
                пациент передвигает ползунок вручную. Уходит как
                user_settings[slug].target_time_hold. */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="cv-params-hold" className="text-sm font-medium text-primary">
                  Время удержания
                </label>
                <span className="min-w-16 rounded-lg border border-border bg-secondary/50 px-2.5 py-1 text-center text-sm font-semibold text-primary">
                  {hold} сек
                </span>
              </div>
              <input
                id="cv-params-hold"
                type="range"
                min={HOLD_MIN}
                max={HOLD_MAX}
                step={HOLD_STEP}
                value={hold}
                onChange={(e) => { setHold(Number(e.target.value)); setSaved(false); }}
                className="w-full accent-primary cursor-pointer"
              />
              <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                <span>{HOLD_MIN} сек</span>
                <span>{HOLD_MAX} сек</span>
              </div>
            </div>

            {error && <p className="text-xs text-red-600">{error}</p>}

            <div className="flex items-center justify-end gap-2">
              {saved && <span className="mr-auto text-xs text-muted-foreground">Сохранено</span>}
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-border px-4 py-2 text-sm font-semibold text-primary hover:bg-secondary"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {saving ? "Сохраняю..." : "Сохранить"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
